/**
 * Plain-language labels for the codes the API speaks. One place, so every page
 * says "In the oven" for c_status 3 and never invents a second wording.
 *
 * Each entry is { label, tone }; `tone` is the closed StatusBadge palette.
 */
import { formatCurrency } from "@/views/Analytics/utils";
import { tr, trf, dateLocale } from "@/i18n";

// panel-i18n words: label
const pick = (map, key) => {
  const k = key === null || key === undefined ? "" : String(key).trim();
  return map[k] ? { ...map[k], label: tr(map[k].label) } : { label: k || "—", tone: "gray" };
};

// mc_orders.c_status — the kitchen workflow state
const C_STATUS = {
  "-1": { label: "Cancelled", tone: "red" },
  "0": { label: "Draft", tone: "gray" },
  "1": { label: "New order", tone: "blue" },
  "2": { label: "Confirmed by store", tone: "cyan" },
  "3": { label: "In the oven", tone: "amber" },
  "4": { label: "Ready", tone: "green" },
  "5": { label: "Out for delivery", tone: "gray" },
};
const PAID = {
  "0": { label: "Not paid yet", tone: "amber" },
  "1": { label: "Paid", tone: "green" },
};
const S_TYPE = {
  "0": { label: "Pickup", tone: "gray" },
  "1": { label: "Delivery", tone: "gray" },
  "4": { label: "In store", tone: "gray" },
};
// mc_orders.platform_type, in the BI dashboard's words (3: the marketplaces, through Deliverect)
const PLATFORM = {
  "0": { label: "Web", tone: "gray" },
  "1": { label: "In-Store", tone: "gray" },
  "2": { label: "Mobile", tone: "gray" },
  "3": { label: "Uber / DoorDash / Skip", tone: "gray" },
};
const MODERATION = {
  clean: { label: "Approved", tone: "green" },
  flagged: { label: "Flagged", tone: "red" },
  pending: { label: "Needs review", tone: "amber" },
};
const CREATION_VIS = {
  draft: { label: "Draft", tone: "gray" },
  published: { label: "Live", tone: "green" },
  hidden: { label: "Hidden", tone: "purple" },
  flagged: { label: "Flagged", tone: "red" },
  deleted: { label: "Deleted", tone: "red" },
};
const GROUP_ORDER = {
  open: { label: "Open · members joining", tone: "green" },
  locked: { label: "Locked · ready to check out", tone: "blue" },
  completed: { label: "Completed", tone: "gray" },
  cancelled: { label: "Cancelled", tone: "red" },
  expired: { label: "Expired", tone: "gray" },
};
const TIER = {
  bronze: { label: "Bronze", tone: "amber" },
  silver: { label: "Silver", tone: "gray" },
  gold: { label: "Gold", tone: "gold" },
  platinum: { label: "Platinum", tone: "purple" },
};

export const cStatus = (k) => pick(C_STATUS, k);
export const paidStatus = (k) => pick(PAID, k);
export const sType = (k) => pick(S_TYPE, k);
export const platform = (k) => pick(PLATFORM, k);
export const moderation = (k) => pick(MODERATION, k);
export const creationVis = (k) => pick(CREATION_VIS, k);
export const groupOrder = (k) => pick(GROUP_ORDER, k);
export const tier = (k) => pick(TIER, k);

/** One label for a creation's combined moderation + visibility state. */
export function creationState(c) {
  if (!c) return { label: "—", tone: "gray" };
  if (c.status === "deleted") return { label: tr("Deleted"), tone: "red" };
  if (c.status === "hidden") return { label: tr("Hidden"), tone: "purple" };
  if (c.moderation_status === "flagged" || c.status === "flagged") return { label: tr("Flagged"), tone: "red" };
  if (c.moderation_status === "pending") return { label: tr("Needs review"), tone: "amber" };
  if (c.moderation_status === "clean") return { label: tr("Approved"), tone: "green" };
  return { label: c.moderation_status || c.status || "—", tone: "gray" };
}

/** How urgent an order's age is, in minutes since it was placed. */
export function ageTone(min) {
  const m = Number(min) || 0;
  if (m >= 30) return "red";
  if (m >= 15) return "amber";
  return "gray";
}

/**
 * "5 min ago" / "2 hours ago" / "Yesterday". Database timestamps are stored in
 * the store's local time, so they are parsed as local time, not UTC.
 */
export function timeAgo(ts) {
  if (!ts) return "";
  const dt = new Date(String(ts).replace(" ", "T"));
  if (isNaN(dt.getTime())) return "";
  const sec = Math.max(1, Math.floor((Date.now() - dt.getTime()) / 1000));
  if (sec < 60) return tr("Just now");
  if (sec < 3600) return trf("{0} min ago", [Math.floor(sec / 60)]);
  if (sec < 86400) return trf("{0} hours ago", [Math.floor(sec / 3600)]);
  if (sec < 172800) return tr("Yesterday");
  if (sec < 604800) return trf("{0} days ago", [Math.floor(sec / 86400)]);
  return dt.toLocaleDateString(dateLocale(), { month: "short", day: "numeric", year: "numeric" });
}

export const money = formatCurrency;

/** "Ada Lovelace", falling back to e-mail, then an id. */
export function personName(first, last, email, idLabel) {
  const n = ((first || "") + " " + (last || "")).trim();
  return n || email || idLabel || "—";
}
